import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { db } from "../../config/firebase";
import { collection, getDocs } from "firebase/firestore";

const Updates = () => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUpdates = async () => {
      try {
        console.log("Fetching updates...");
        const updatesCollection = collection(db, "updates");
        const updatesSnapshot = await getDocs(updatesCollection);
        const updatesList = updatesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        console.log("Updates fetched:", updatesList);
        setUpdates(updatesList);
      } catch (error) {
        console.error("Error fetching updates:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUpdates();
  }, []);

  // Format date function
  const formatDate = (timestamp) => {
    if (!timestamp) return "";

    // Convert Firestore timestamp to Date object
    const date = timestamp.toDate();

    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    }).format(date);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 dark:border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section - Dynamic Showcase Design with Blue Theme */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 text-white">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Geometric shapes */}
          <motion.div 
            className="absolute top-[15%] right-[10%] w-64 h-64 rounded-full bg-blue-500/10 blur-3xl"
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-[15%] left-[15%] w-80 h-80 rounded-full bg-blue-400/10 blur-3xl"
            animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
          
          {/* Dynamic lines */}
          <div className="absolute inset-0 opacity-10">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 0 10 L 40 10" fill="none" stroke="white" strokeWidth="0.5" />
                  <path d="M 10 0 L 10 40" fill="none" stroke="white" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>
        </div>

        {/* Main content container */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 py-16 md:py-24 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Left content - Text and CTA */}
            <div className="lg:col-span-6 lg:pr-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="inline-block mb-4 px-4 py-1 rounded-full bg-blue-500/20 backdrop-blur-sm border border-blue-500/30">
                  <span className="text-blue-300 text-sm font-semibold tracking-wider">
                    NEWS & ANNOUNCEMENTS
                  </span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                  Latest{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-blue-400 to-blue-200">
                    Updates
                  </span>
                </h1>
                
                <p className="text-lg md:text-xl mb-8 text-gray-200 font-medium leading-relaxed max-w-xl">
                  Stay informed with our latest news, achievements, and important announcements from AZ Boys Gymnastics.
                </p>
              </motion.div>
            </div>
            
            {/* Right content - Hero image with animated elements */}
            <div className="lg:col-span-6 relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.2 }}
                className="relative"
              >
                {/* Main image container with mask effect */}
                <div className="relative overflow-hidden rounded-2xl shadow-2xl border border-white/10 bg-gradient-to-br from-blue-500/10 to-blue-700/10 backdrop-blur-sm">
                  <div className="aspect-[4/3] relative overflow-hidden">
                    <img 
                      src="/updateshero.png" 
                      alt="AZ Boys Gymnastics Updates" 
                      className="w-full h-full object-cover object-center"
                    />
                    
                    {/* Overlay gradient */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-blue-900/40 via-transparent to-transparent"></div>
                    
                    {/* Animated highlight */}
                    <motion.div 
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/20 to-transparent" 
                      style={{ width: '200%', x: '-100%' }}
                      animate={{ x: '100%' }}
                      transition={{ duration: 2.5, repeat: Infinity, repeatType: 'loop', ease: 'linear', repeatDelay: 5 }}
                    />
                  </div>
                </div>
                
                {/* Decorative elements */}
                <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-blue-500/30 backdrop-blur-xl border border-blue-400/30 flex items-center justify-center">
                  <span className="text-white font-bold text-lg">AZ</span>
                </div>
                
                <motion.div 
                  className="absolute -bottom-4 -left-4 p-4 bg-white/10 backdrop-blur-xl rounded-lg border border-white/20 shadow-xl"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-white font-medium">Stay Connected</div>
                      <div className="text-xs text-gray-300">Latest News & Achievements</div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden lg:flex flex-col items-center"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          <span className="text-sm text-gray-300 mb-2">Scroll to explore</span>
          <svg
            className="w-6 h-6 text-blue-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </motion.div>
      </section>

      {/* Updates Grid with Enhanced Layout */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="mb-10 text-center">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Latest News & Updates</h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">Stay informed about competitions, achievements, and important announcements from the Arizona Boys Gymnastics community.</p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {updates.length > 0 ? (
            updates.map((update, index) => (
              <motion.div
                key={update.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all border border-gray-200 dark:border-gray-700 group hover:border-blue-300 dark:hover:border-blue-500"
              >
                {/* Card header with image if available */}
                {update.imageUrl && (
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={update.imageUrl} 
                      alt={update.title} 
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  </div>
                )}
                
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <span className="px-3 py-1 bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-300 text-sm font-medium rounded-full">
                      {update.category || "General"}
                    </span>
                    <time className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(update.createdAt)}
                    </time>
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {update.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-3">
                    {update.description || update.content}
                  </p>
                  <div className="flex justify-between items-center">
                    {update.url && (
                      <a
                        href={update.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm"
                      >
                        External Link →
                      </a>
                    )}
                    <Link
                      to={`/updates/${update.id}`}
                      className="inline-flex items-center text-blue-600 dark:text-blue-400 font-semibold hover:text-blue-700 dark:hover:text-blue-300 group"
                    >
                      Read More
                      <svg
                        className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-600 dark:text-gray-400">
                No updates available at the moment.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Updates;
